/**
 * Composable for managing dark mode state across the application.
 *
 * The `.dark` class on <html> is applied BEFORE first paint by the inline
 * script in nuxt.config.js. This composable only mirrors and mutates that
 * state — it never races to be the thing that first applies it. It used to,
 * as a module-level side effect, which meant the theme only landed once some
 * route or layout chunk happened to import this file: the same URL rendered
 * light on a direct load and dark when reached via a redirect.
 */

const STORAGE_KEY = 'theme-preference';

// The pre-paint script has already resolved storage into the class, so the DOM
// is the single source of truth and the two can never drift apart.
const readTheme = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

// Shared reactive state (singleton pattern)
const isDark = ref(readTheme());
const isInitialized = ref(false);

const applyDarkClass = (dark) => {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  // Suppress colour transitions across the swap. Flipping the class only
  // changes custom property VALUES, and Chrome holds the previously computed
  // colour for any element mid-colour-transition — which left every
  // `transition-colors` surface painted from a --nviya-* token stuck on the
  // outgoing theme. The layout shell never re-mounts, so it stayed stuck for
  // the rest of the session. See the [data-theme-switching] rule in main.css.
  el.setAttribute('data-theme-switching', '');
  el.classList.toggle('dark', dark);
  void el.offsetHeight; // force the recalc to happen while transitions are off
  // A timer, not requestAnimationFrame: rAF never fires in a background tab,
  // which would strand the attribute and kill transitions for good.
  setTimeout(() => el.removeAttribute('data-theme-switching'), 0);
};

const persist = (dark) => {
  try {
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  } catch (e) {
    // Private mode or blocked storage — the class still applies for this session.
  }
};

export const useDarkMode = () => {
  // Re-sync from the DOM. Cheap and idempotent, so it is safe for a layout to
  // call on every mount.
  const initialize = () => {
    if (typeof document === 'undefined') return;
    isDark.value = readTheme();
    isInitialized.value = true;
  };

  // Set dark mode explicitly
  const setDarkMode = (value) => {
    isDark.value = value;
    if (typeof document === 'undefined') return;
    persist(value);
    applyDarkClass(value);
  };

  // Toggle dark mode
  const toggleTheme = () => setDarkMode(!isDark.value);

  if (typeof document !== 'undefined' && !isInitialized.value) {
    initialize();
  }

  return {
    isDark,
    isInitialized,
    initialize,
    toggleTheme,
    setDarkMode
  };
};
